import React from "react";
import { getCreditsActivity, startCreditCheckout } from "../lib/api";
import type { AuthHeaders } from "../lib/auth";
import type { CreditsActivity, CreditTxn } from "../lib/types";
import { Unauthorized } from "../lib/api";
import { fmtDate } from "../lib/format";
import { landingUrl } from "../lib/urls";
import {
  Callout,
  Button,
  Card,
  EmptyState,
  Grid,
  Icon,
  IconTile,
  LinkButton,
  MoneyInput,
  Page,
  SectionHead,
  Skeleton,
  StatTile,
  Table,
  type IconName,
  type IconTone,
  parseMoney,
} from "../design";
import { usdShort } from "./insights/util";

const docs = (path: string): string => `${landingUrl()}/docs/${path}`;

function fmtUsd(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtTokens(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

export function Credits({ authHeaders }: { authHeaders: AuthHeaders }) {
  const [data, setData] = React.useState<CreditsActivity | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [paid, setPaid] = React.useState(false);

  const load = React.useCallback(() => {
    setErr(null);
    getCreditsActivity(authHeaders)
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => {
        if (e instanceof Unauthorized) return;
        setErr("Could not load credits.");
      });
  }, [authHeaders]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("topup") === "ok") {
      setPaid(true);
      params.delete("topup");
      const qs = params.toString();
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${qs ? `?${qs}` : ""}#credits`,
      );
    }
    load();
  }, [load]);

  const actions = (
    <>
      <Button variant="ghost" onClick={load}>
        Refresh
      </Button>
      <Button
        variant="ghost"
        icon="keys"
        onClick={() => {
          window.location.hash = "keys?new=1";
        }}
      >
        Create key
      </Button>
    </>
  );

  if (err) {
    return (
      <Page title="Credits">
        <EmptyState
          icon="alert"
          title={err}
          action={<Button onClick={load}>Retry</Button>}
        />
      </Page>
    );
  }

  if (!data) {
    return (
      <Page title="Credits">
        <Skeleton height={96} />
      </Page>
    );
  }

  if (!data.available) {
    return (
      <Page title="Credits">
        <EmptyState icon="users" title="Billed through your organization">
          Members bill through the shared organization credit pool. Ask an org
          admin to top it up.
        </EmptyState>
      </Page>
    );
  }

  return (
    <Page
      title="Credits"
      scope={data.account_email || undefined}
      actions={actions}
    >
      {paid && (
        <Callout tone="ok" role="status" title="Payment received">
          Your balance below reflects the new credits.
        </Callout>
      )}

      <Grid cols={3}>
        <CreditStatBoxes data={data} />
        <BuyRow
          data={data}
          checkout={(n) => startCreditCheckout(authHeaders, n)}
          onDone={load}
        />
      </Grid>

      <Transactions txns={data.transactions} />

      <Models />

      <Resources />
    </Page>
  );
}

/** Days the balance lasts at the trailing 30-day spend rate; null with no spend. */
export function runwayDays(data: CreditsActivity): number | null {
  const balance = Number(data.balance);
  const spent = Number(data.spent_30d);
  if (!(spent > 0) || !Number.isFinite(balance)) return null;
  return Math.max(0, Math.round((balance / spent) * 30));
}

// The ledger names fee lines after the engine; members read what they paid for.
function txnLabel(label: string): string {
  return label === "Compression" ? "Compressed requests" : label;
}

export function RunwayNote({ days }: { days: number | null }) {
  if (days === null) return <>Credits never expire</>;
  if (days > 365)
    return <>At your last 30 days&apos; pace, credits last over a year</>;
  return (
    <span style={days < 14 ? { color: "var(--ds-warn)" } : undefined}>
      At your last 30 days&apos; pace, credits last about {days} more{" "}
      {days === 1 ? "day" : "days"}
    </span>
  );
}

export function CreditStatBoxes({ data }: { data: CreditsActivity }) {
  const balance = Number(data.balance);
  const spent = Number(data.spent_30d);
  const funded = Number(data.funded_30d);
  const spentPct =
    funded > 0 ? Math.min(100, Math.max(0, (spent / funded) * 100)) : 0;
  return (
    <>
      <StatTile
        label="Balance"
        info="Prepaid USD for condense's own fee and models. Your model provider bills its tokens separately. Credits never expire."
        value={usdShort(balance)}
        note={<RunwayNote days={runwayDays(data)} />}
      />
      <StatTile
        label="Credits used, last 30 days"
        info="condense's fee for compressing your traffic. Your model provider bills its tokens separately; that is Spend on My usage, so the two never match."
        value={usdShort(spent)}
        note={`${fmtTokens(data.tokens_removed_30d)} tokens saved`}
        spark={
          funded > 0 && (
            <span
              className="per-meter"
              role="meter"
              aria-label="Share of funded credits used"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(spentPct)}
            >
              <span
                style={{
                  width: `${spentPct}%`,
                  background:
                    balance <= 0 ? "var(--ds-bad)" : "var(--ds-spent)",
                }}
              />
            </span>
          )
        }
      />
    </>
  );
}

export function BuyRow({
  data,
  checkout,
  onDone,
}: {
  data: CreditsActivity;
  checkout: (usd: number) => Promise<string>;
  onDone: () => void;
}) {
  const [amount, setAmount] = React.useState<string>(
    String(data.topup_min_usd || 5),
  );
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const n = parseMoney(amount) ?? NaN;
  const valid =
    Number.isFinite(n) && n >= data.topup_min_usd && n <= data.topup_max_usd;

  async function buy() {
    if (!valid || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const url = await checkout(n);
      window.location.href = url;
    } catch {
      setErr(
        "Couldn't open the payment page. Nothing was charged; try again in a minute.",
      );
      setBusy(false);
      onDone();
    }
  }

  return (
    <Card title="Add credits" info="One-time top-up. Credits never expire.">
      <div className="per-buy">
        <MoneyInput
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          aria-label="Amount in USD"
        />
        <Button variant="primary" disabled={!valid} busy={busy} onClick={buy}>
          {busy ? "Redirecting…" : "Continue to payment"}
        </Button>
      </div>
      <p className="per-muted" style={{ marginTop: "var(--sp-2)" }}>
        {Number.isFinite(n) && n > 0 && n < data.topup_min_usd
          ? `The minimum top-up is $${data.topup_min_usd}.`
          : n > data.topup_max_usd
            ? `The most you can add at once is $${data.topup_max_usd.toLocaleString("en-US")}.`
            : `Between $${data.topup_min_usd} and $${data.topup_max_usd.toLocaleString("en-US")}. You confirm the amount and pay on the next page.`}
      </p>
      {err && (
        <div className="per-err" role="alert">
          {err}
        </div>
      )}
    </Card>
  );
}

type Model = {
  name: string;
  id: string;
  desc: string;
};

const MODELS: Model[] = [
  {
    name: "Helene 1",
    id: "helene-1",
    desc: "The default. Fast and accurate for everyday coding sessions.",
  },
  {
    name: "Adeline 1",
    id: "adeline-1",
    desc: "For long agent sessions with lots of history. Compresses big transcripts quickly.",
  },
];

// Diffusion motif behind each model id: faint monospace noise with accent
// glyphs shimmering along the right ("compacted") edge. Ported from the docs
// model cards (landing/src/docs.ts initModelArt); honors reduced-motion.
function ModelArt() {
  const ref = React.useRef<HTMLCanvasElement>(null);
  React.useEffect(() => {
    const canvas = ref.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !host || !ctx) return;

    const cs = getComputedStyle(document.documentElement);
    const fg = cs.getPropertyValue("--fg-4").trim() || "gray";
    const accent = cs.getPropertyValue("--accent").trim() || "royalblue";
    const NOISE = "0123456789{}[]()<>=+*/;:.$#%&";
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const TAU = Math.PI * 2;
    const waves = [
      { c: Math.cos(0.5), s: Math.sin(0.5), sp: 26, wl: 120, p: 0 },
      { c: Math.cos(2.1), s: Math.sin(2.1), sp: 18, wl: 90, p: 1.7 },
      { c: Math.cos(-1), s: Math.sin(-1), sp: 22, wl: 150, p: 3.1 },
    ];
    const act = (x: number, y: number, t: number): number => {
      let a = 0;
      for (const w of waves) {
        const d = x * w.c + y * w.s;
        a += 0.5 + 0.5 * Math.sin(((d - t * w.sp) / w.wl) * TAU + w.p);
      }
      return a / waves.length;
    };

    const gap = 22;
    type Cell = {
      x: number;
      y: number;
      band: boolean;
      gi: number;
      churn: number;
    };
    let cells: Cell[] = [];
    let w = 0;
    let h = 0;

    const resize = (): void => {
      w = host.clientWidth;
      h = host.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = `600 13px ${cs.getPropertyValue("--ds-mono").trim() || "monospace"}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      cells = [];
      for (let y = gap / 2; y < h; y += gap) {
        for (let x = gap / 2; x < w; x += gap) {
          cells.push({
            x,
            y,
            band: x > w - gap * 3.2,
            gi: (Math.random() * NOISE.length) | 0,
            churn: 0,
          });
        }
      }
    };

    const frame = (now: number): void => {
      if (!w || !h) return;
      const t = now / 1000;
      ctx.clearRect(0, 0, w, h);
      for (const c of cells) {
        const a = act(c.x, c.y, t);
        let alpha: number;
        let color: string;
        if (c.band && a > 0.62) {
          if (now - c.churn > 220) {
            c.gi = (Math.random() * NOISE.length) | 0;
            c.churn = now;
          }
          color = accent;
          alpha = 0.26 + 0.4 * Math.min(1, (a - 0.62) / 0.38);
        } else {
          if (a > 0.72 && now - c.churn > 90) {
            c.gi = (c.gi + 1) % NOISE.length;
            c.churn = now;
          }
          color = fg;
          alpha = 0.07 + 0.32 * a;
        }
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color;
        ctx.fillText(NOISE.charAt(c.gi), c.x, c.y);
      }
      ctx.globalAlpha = 1;
    };

    resize();
    const ro = new ResizeObserver(() => resize());
    ro.observe(host);

    if (reduce) {
      frame(0);
      return () => ro.disconnect();
    }
    let raf = 0;
    let last = 0;
    const loop = (now: number): void => {
      if (!canvas.isConnected) return;
      if (now - last >= 55) {
        last = now;
        frame(now);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);
  return <canvas ref={ref} aria-hidden="true" />;
}

export function Models() {
  return (
    <section>
      <SectionHead
        title="Models"
        actions={
          <LinkButton
            variant="link"
            size="sm"
            href={docs("models/")}
            iconRight="arrow-up-right"
          >
            Compare models
          </LinkButton>
        }
      />
      <div className="model-grid">
        {MODELS.map((m) => (
          <a className="model-card" key={m.id} href={docs(`models/#${m.id}`)}>
            <div className="model-art">
              <ModelArt />
              <span className="model-art-id">{m.id}</span>
            </div>
            <div className="model-card-body">
              <div className="model-card-head">
                <span className="model-name">{m.name}</span>
              </div>
              <p className="model-desc">{m.desc}</p>
              <span className="model-more">
                View model <Icon name="arrow-right" size={14} />
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

type Resource = {
  name: string;
  desc: string;
  path: string;
  icon: IconName;
  tone: IconTone;
};

const RESOURCES: Resource[] = [
  {
    name: "Quickstart",
    desc: "Make your first compressed request through the proxy in a few minutes.",
    path: "quickstart/",
    icon: "sparkle",
    tone: "blue",
  },
  {
    name: "API reference",
    desc: "Authentication, the two run modes, headers and every endpoint.",
    path: "reference/",
    icon: "resources",
    tone: "violet",
  },
  {
    name: "Rate limits",
    desc: "How much one account can push through, and what to do at a cap.",
    path: "rate-limits/",
    icon: "clock",
    tone: "amber",
  },
  {
    name: "Security",
    desc: "How condense handles your data security and privacy.",
    path: "security/",
    icon: "lock",
    tone: "green",
  },
];

export function Resources() {
  return (
    <section>
      <SectionHead title="Resources" />
      <Grid cols={4}>
        {RESOURCES.map((r) => (
          <a className="ds-card per-res" href={docs(r.path)} key={r.name}>
            <span className="per-res-head">
              <IconTile icon={r.icon} tone={r.tone} />
              {r.name}
            </span>
            <span className="per-row-sub">{r.desc}</span>
          </a>
        ))}
      </Grid>
    </section>
  );
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// group key by local calendar day, matching fmtDate's local rendering
function dayKeyOf(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

type UsageRollup = {
  type: "rollup";
  day: string;
  items: CreditTxn[];
  total: number;
};
type LedgerRow = { type: "txn"; txn: CreditTxn } | UsageRollup;

// collapse each day's usage events into one rollup row; discrete events
// (top-ups, welcome credits, refunds) stay as their own rows in place.
function buildLedger(txns: CreditTxn[]): LedgerRow[] {
  const rows: LedgerRow[] = [];
  const rollupAt = new Map<string, number>();
  for (const t of txns) {
    if (t.kind === "usage") {
      const key = dayKeyOf(t.at);
      const idx = rollupAt.get(key);
      if (idx !== undefined) {
        const r = rows[idx] as UsageRollup;
        r.items.push(t);
        r.total += Number(t.amount);
      } else {
        rollupAt.set(key, rows.length);
        rows.push({
          type: "rollup",
          day: fmtDate(t.at),
          items: [t],
          total: Number(t.amount),
        });
      }
    } else {
      rows.push({ type: "txn", txn: t });
    }
  }
  return rows;
}

function expandProps(expandable: boolean, open: boolean, toggle: () => void) {
  if (!expandable) return {};
  return {
    className: "click",
    tabIndex: 0,
    "aria-expanded": open,
    onClick: toggle,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggle();
      }
    },
  };
}

function signed(amt: number): string {
  return `${amt >= 0 ? "+" : "−"}$${fmtUsd(Math.abs(amt))}`;
}

function TxnRow({ t }: { t: CreditTxn }) {
  const [open, setOpen] = React.useState(false);
  const amt = Number(t.amount);
  const detail = t.detail ?? [];
  const expandable = detail.length > 0;
  return (
    <>
      <tr {...expandProps(expandable, open, () => setOpen((v) => !v))}>
        <td className="muted">{fmtDate(t.at)}</td>
        <td>{txnLabel(t.label)}</td>
        <td className={`num${amt >= 0 ? " per-credit" : ""}`}>{signed(amt)}</td>
      </tr>
      {open && (
        <tr className="per-subrow">
          <td className="muted">{t.detail_title ?? "Detail"}</td>
          <td className="muted" colSpan={2}>
            {detail.map((d, i) => (
              <span key={i} className="per-detail">
                {d.label}
                {d.amount ? ` · $${fmtUsd(Math.abs(Number(d.amount)))}` : ""}
              </span>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

function RollupRow({ r }: { r: UsageRollup }) {
  const [open, setOpen] = React.useState(false);
  const count = r.items.length;
  const expandable = count > 1;
  return (
    <>
      <tr {...expandProps(expandable, open, () => setOpen((v) => !v))}>
        <td className="muted">{r.day}</td>
        <td>
          Usage
          <span className="muted">
            {" "}
            · {count} {count === 1 ? "request" : "requests"}
          </span>
        </td>
        <td className="num">−${fmtUsd(Math.abs(r.total))}</td>
      </tr>
      {open &&
        r.items.map((t, i) => (
          <tr className="per-subrow" key={i}>
            <td className="muted">{fmtTime(t.at)}</td>
            <td className="muted">{txnLabel(t.label)}</td>
            <td className="num muted">{signed(Number(t.amount))}</td>
          </tr>
        ))}
    </>
  );
}

export function Transactions({
  txns,
}: {
  txns: CreditsActivity["transactions"];
}) {
  const rows = React.useMemo(() => buildLedger(txns), [txns]);
  return (
    <Card
      flush
      title="Recent transactions"
      info="The latest top-ups and charges on your balance, with each day's charges in one row. Credits used counts every charge in the last 30 days, so it can be more than the rows shown."
    >
      {txns.length === 0 ? (
        <EmptyState compact icon="credits" title="No transactions yet" />
      ) : (
        <Table label="Recent transactions" className="per-ledger">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) =>
              r.type === "rollup" ? (
                <RollupRow key={i} r={r} />
              ) : (
                <TxnRow key={i} t={r.txn} />
              ),
            )}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
